import type { HTTP } from "@/core/http";
import {
  enumFrom,
  type IssueApiResponse,
  type IssueCreateRequest,
  type IssueHistoryApiResponse,
  type IssueMetadataApiResponse,
  type IssueUpdateRequest,
} from "@/shared/api";
import { dateToLocalYYYYMMDD } from "@/shared/utils/dateFormat";
import { downloadFromResponse } from "@/shared/utils/downloadBlob";
import type { Issue, IssueFilters, IssueHistory, IssueMetadata } from "../types";

export type { Issue };

const ISSUES_BASE = "/pms/issues";

function strEnum(raw: unknown): string | undefined {
  return enumFrom(raw) ?? (typeof raw === "string" ? raw : undefined);
}

/** Payload for POST /pms/issues — enums as plain strings. */
function toApiCreate(data: Partial<Issue>): IssueCreateRequest {
  return {
    projectId: data.projectId ?? 0,
    issueShortDescription: data.issueShortDescription ?? "",
    issueDescription: data.issueDescription ?? "",
    issueIdentificationDate: data.issueIdentificationDate ?? "",
    issueImpact: strEnum(data.issueImpact) ?? "",
    issueUrgency: strEnum(data.issueUrgency) ?? "",
    issueSeverity: strEnum(data.issueSeverity) ?? "",
    issueCause: data.issueCause ?? undefined,
    issueActionOwner: data.issueActionOwner ?? undefined,
    issueType: strEnum(data.issueType) ?? "",
    issueResolutionPlan: data.issueResolutionPlan ?? undefined,
    issueResolutionActions: data.issueResolutionActions ?? undefined,
    plannedIssueClosureDate: data.plannedIssueClosureDate ?? undefined,
    issueRemarks: data.issueRemarks ?? undefined,
  };
}

/** Payload for PUT /pms/issues/{issueId} — partial; enums as strings. */
function toApiUpdate(data: Partial<Issue>): IssueUpdateRequest {
  const out: IssueUpdateRequest = {};
  if (data.issueStatus !== undefined) out.issueStatus = strEnum(data.issueStatus);
  if (data.issueShortDescription !== undefined) {
    out.issueShortDescription = data.issueShortDescription;
  }
  if (data.issueDescription !== undefined) out.issueDescription = data.issueDescription;
  if (data.issueImpact !== undefined) out.issueImpact = strEnum(data.issueImpact);
  if (data.issueUrgency !== undefined) out.issueUrgency = strEnum(data.issueUrgency);
  if (data.issueSeverity !== undefined) out.issueSeverity = strEnum(data.issueSeverity);
  if (data.issueType !== undefined) out.issueType = strEnum(data.issueType);
  // Preserve null so clears survive JSON.stringify (undefined keys are omitted).
  if (data.issueCause !== undefined) out.issueCause = data.issueCause;
  if (data.issueResolutionPlan !== undefined) {
    out.issueResolutionPlan = data.issueResolutionPlan;
  }
  if (data.issueResolutionActions !== undefined) {
    out.issueResolutionActions = data.issueResolutionActions;
  }
  if (data.issueActionOwner !== undefined) {
    out.issueActionOwner = data.issueActionOwner;
  }
  if (data.plannedIssueClosureDate !== undefined) {
    out.plannedIssueClosureDate = data.plannedIssueClosureDate;
  }
  if (data.actualIssueClosureDate !== undefined) {
    out.actualIssueClosureDate = data.actualIssueClosureDate;
  }
  if (data.issueRemarks !== undefined) out.issueRemarks = data.issueRemarks;
  return out;
}

export class IssueService {
  constructor(private readonly httpClient: HTTP) {}

  /**
   * GET /pms/issues/metadata — severity, urgency, impact, type, status (enum + label).
   */
  async getIssueMetadata(): Promise<IssueMetadata> {
    return this.httpClient.get<IssueMetadataApiResponse>(`${ISSUES_BASE}/metadata`);
  }

  /**
   * GET /pms/issues/project/{projectId}?status=OPEN
   * Pass status from metadata enum values (e.g. OPEN, ACTIVE, CLOSED) as-is.
   */
  async getIssues(filters?: IssueFilters): Promise<Issue[]> {
    if (!filters?.projectId) return [];
    const params: Record<string, string | string[]> = {};
    if (filters.status?.length) {
      params.status = filters.status;
    }
    const list = await this.httpClient.get<IssueApiResponse[]>(
      `${ISSUES_BASE}/project/${filters.projectId}`,
      { params },
    );
    return Array.isArray(list) ? list : [];
  }

  /**
   * GET /pms/issues/{issueId}/history
   */
  async getIssueHistory(issueId: number): Promise<IssueHistory[]> {
    const list = await this.httpClient.get<IssueHistoryApiResponse[]>(
      `${ISSUES_BASE}/${issueId}/history`,
    );
    return Array.isArray(list) ? list : [];
  }

  /**
   * PUT /pms/issues/{issueId}
   */
  async updateIssue(issueId: number, data: Partial<Issue>): Promise<Issue> {
    return this.httpClient.put<IssueApiResponse, IssueUpdateRequest>(
      `${ISSUES_BASE}/${issueId}`,
      { body: toApiUpdate(data) },
    );
  }

  /**
   * POST /pms/issues
   */
  async createIssue(data: Partial<Issue>): Promise<Issue> {
    return this.httpClient.post<IssueApiResponse, IssueCreateRequest>(
      ISSUES_BASE,
      { body: toApiCreate(data) },
    );
  }

  /**
   * Download issues report for a project.
   */
  async downloadIssuesReport(
    projectId: number,
    format: "csv" | "pdf" | "excel" = "csv",
    autoDownload = true,
  ): Promise<Blob> {
    const response = await this.httpClient.get<Response>(
      `${ISSUES_BASE}/project/${projectId}/report`,
      {
        params: { format },
        returnRawResponse: true,
      },
    );

    return downloadFromResponse(
      response,
      `issues_report_${projectId}_${dateToLocalYYYYMMDD(new Date())}.${format}`,
      autoDownload,
    );
  }
}
